import React, { useState, useEffect } from 'react';
import { FiPlus, FiPackage, FiEdit2, FiInfo } from 'react-icons/fi';
import { useNotification } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import { hasBillingRole } from '../utils/roles';
import PermissionButton from './PermissionButton';
import {
  FEE_CATEGORIES,
  BILLING_UNITS,
  FEE_SCHEDULES,
  STORAGE_CLASSES
} from '../utils/customerFeeCatalog';

// Storage classes we render as default rows in the UI even when no DB
// row exists yet. The operator can leave either at $0 to disable it,
// or set real rates for one or both. Matches the standard / oversize
// split in services.xlsx.
const DEFAULT_STORAGE_CLASSES_TO_SHOW = ['standard', 'oversize'];

// Composite key helper. Storage fees need a storage class to be
// unique (a customer can have standard + oversize + hazmat rows for
// the same warehouse at different rates). Other fee types don't use
// the suffix, so their key is just the fee_type.
const makeFeeKey = (feeType, storageClass) => {
  if (feeType === 'storage_fee') return `storage_fee|${storageClass || 'standard'}`;
  return feeType;
};

// Mirror of billingService.normalizeStorageClass: 'rackable' is the
// legacy spelling of 'standard', and a missing class means standard.
// The form groups storage rows by this normalized slot so it edits the
// same row billing resolves -- rendering legacy duplicates as their own
// editable rows made every Save All predictably 409 on the shadowed one.
const normalizeStorageClass = (storageClass) =>
  (!storageClass || storageClass === 'rackable') ? 'standard' : storageClass;

// Mirror of billing's slot precedence (buildStorageFeeLookup restricted
// to active rows): active rows first (billing only reads active), then
// explicit standard > null-class > legacy spellings, then id tie-break.
const storageSlotRank = (fee) => {
  if (fee.storage_class === 'standard') return 0;
  if (fee.storage_class == null) return 1;
  return 2;
};

const pickStorageSlotWinner = (candidates) =>
  [...candidates].sort((a, b) => {
    const aActive = a.is_active === false ? 1 : 0;
    const bActive = b.is_active === false ? 1 : 0;
    if (aActive !== bActive) return aActive - bActive;
    const rankDiff = storageSlotRank(a) - storageSlotRank(b);
    if (rankDiff !== 0) return rankDiff;
    return String(a.id) < String(b.id) ? -1 : 1;
  })[0];

const CustomerFeesManager = ({ customerId, showWarehouseAccessOnly = false, showFeesOnly = false }) => {
  const [loading, setLoading] = useState(true);
  const [fees, setFees] = useState([]);
  const [destinationWarehouseAccess, setDestinationWarehouseAccess] = useState([]);
  const [destinationWarehouses, setDestinationWarehouses] = useState([]);
  const [selectedDestinationWarehouseId, setSelectedDestinationWarehouseId] = useState('');
  const [showDestinationAccessForm, setShowDestinationAccessForm] = useState(false);
  const { showSuccess, showError } = useNotification();
  // Fee mutations are role-gated on the backend (RF15).
  const { user } = useAuth();
  const canMutateBilling = hasBillingRole(user);

  // Comprehensive fee form state for all fee types
  const [allFeesForm, setAllFeesForm] = useState({});

  // Access form state
  const [destinationAccessForm, setDestinationAccessForm] = useState({
    warehouse_id: '',
    notes: ''
  });

  // Fee type metadata comes from the shared catalog (aligned with
  // services.xlsx). The manager only shows non-deprecated categories
  // in the main table, but still reads the deprecated ones when
  // hydrating existing fee rows so legacy data continues to render.
  //
  // `feeTypes` here is a flat array mapped into the existing component
  // shape (value/label/defaultUnit/defaultSchedule/category). Storage
  // is excluded from this flat list because it is rendered in a
  // dedicated section above the main table (one row per storage class
  // with its own discount columns).
  // Only render active (non-hidden) categories, and skip storage
  // because storage has its own dedicated section above the main
  // grid (storage rows carry extra columns: storage_class + discount
  // fields). Under the simplified catalog the main grid now only
  // contains Handling (Inbound / Outbound / Labour).
  const feeTypes = FEE_CATEGORIES
    .filter((cat) => !cat.hidden && cat.id !== 'storage')
    .flatMap((cat) => cat.fees.map((fee) => ({
      value: fee.value,
      label: fee.label,
      description: fee.description,
      defaultUnit: fee.defaultBillingUnit,
      defaultSchedule: fee.defaultFeeSchedule,
      category: cat.label
    })));
  // Legacy inbound receiving callout is no longer relevant -- the
  // services are included in Handling — Inbound. Kept as an empty
  // array so the callout block below renders empty and effectively
  // disappears without having to delete the JSX.
  const inboundReceivingFeeTypes = [];

  // Storage metadata pulled once for the dedicated storage section
  // below. The catalog defines the single storage_fee entry; the UI
  // splits it into one row per storage class.
  const storageFeeMeta = FEE_CATEGORIES.find((c) => c.id === 'storage')?.fees?.[0] || null;

  const billingUnits = BILLING_UNITS;
  const feeSchedules = FEE_SCHEDULES;

  useEffect(() => {
    fetchData();
  }, [customerId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [feesRes, accessRes, warehousesRes] = await Promise.all([
        fetch(`/api/customer-fees/${customerId}/fees`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch(`/api/customer-fees/${customerId}/destination-warehouse-access`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch('/api/customer-fees/warehouses', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      const [feesData, accessData, warehousesData] = await Promise.all([
        feesRes.json(),
        accessRes.json(),
        warehousesRes.json()
      ]);

      if (feesData.success) {
        setFees(feesData.data);
        initializeFeesForm(feesData.data);
      }
      if (accessData.success) setDestinationWarehouseAccess(accessData.data);
      if (warehousesData.success) setDestinationWarehouses(warehousesData.data);
    } catch (error) {
      showError('Failed to load customer fees data');
    } finally {
      setLoading(false);
    }
  };

  // Get active warehouse IDs for fee form
  const activeDestinationWarehouseIds = destinationWarehouseAccess
    .filter(access => access.is_active)
    .map(access => access.warehouse_id);

  // Initialize comprehensive fees form with existing data.
  //
  // The form is keyed by warehouse, then by a composite fee key that
  // for storage_fee includes the storage_class so that a warehouse
  // can hold multiple storage_fee rows (standard / oversize / etc.)
  // at once. All other fee types key by fee_type alone.
  const initializeFeesForm = (existingFees) => {
    const form = {};

    const warehouseIds = destinationWarehouseAccess
      .filter(access => access.is_active)
      .map(access => access.warehouse_id);

    warehouseIds.forEach(warehouseId => {
      form[warehouseId] = {};

      // Storage fees -- one entry per storage class. Always seed the
      // default classes (standard + oversize) even when no DB row
      // exists yet, so the operator sees both available rows.
      if (storageFeeMeta) {
        const classesToShow = new Set(DEFAULT_STORAGE_CLASSES_TO_SHOW);
        // Also include any other storage classes that exist on the DB
        // for this warehouse so legacy data renders. Classes are
        // NORMALIZED into slots (rackable/null -> standard) so a legacy
        // duplicate never renders beside the canonical row it shadows.
        existingFees
          .filter(fee => fee.warehouse_id === warehouseId && fee.fee_type === 'storage_fee')
          .forEach(fee => classesToShow.add(normalizeStorageClass(fee.storage_class)));

        classesToShow.forEach((slotClass) => {
          const key = makeFeeKey('storage_fee', slotClass);
          // One row per slot, editing the SAME row billing resolves for
          // that slot. Shadowed duplicates (e.g. an active rackable row
          // losing to an active standard row) are not rendered, so Save
          // All never submits a row the backend will 409 as shadowed.
          const candidates = existingFees.filter(fee =>
            fee.warehouse_id === warehouseId
            && fee.fee_type === 'storage_fee'
            && normalizeStorageClass(fee.storage_class) === slotClass
          );
          const existingFee = candidates.length > 0 ? pickStorageSlotWinner(candidates) : null;
          form[warehouseId][key] = {
            fee_type: 'storage_fee',
            // The row's ACTUAL stored class, VERBATIM -- null stays null.
            // Coercing null to the slot class made saving a null-class
            // winner an explicit class change, which the backend 409s
            // when an inactive same-class sibling occupies that unique
            // slot. New rows get the canonical slot class.
            storage_class: existingFee ? existingFee.storage_class : slotClass,
            storage_class_slot: slotClass,
            fee_amount: existingFee?.fee_amount || '',
            billing_unit: existingFee?.billing_unit || storageFeeMeta.defaultBillingUnit,
            fee_schedule: existingFee?.fee_schedule || storageFeeMeta.defaultFeeSchedule,
            volume_discount_rate: existingFee?.volume_discount_rate || '',
            volume_discount_threshold: existingFee?.volume_discount_threshold || '',
            longterm_discount_rate: existingFee?.longterm_discount_rate || '',
            longterm_discount_months: existingFee?.longterm_discount_months || '',
            notes: existingFee?.notes || '',
            id: existingFee?.id || null
          };
        });
      }

      // Non-storage fees -- one entry per fee type. When legacy duplicates
      // exist, edit the CANONICAL row billing actually uses: the
      // null-class row wins, then lowest id -- matching the backend's
      // deterministic fee resolution. Picking the first arbitrary match
      // let admins "successfully" edit a row billing ignores.
      feeTypes.forEach(feeType => {
        // Insurance is company-wide: hydrate the canonical row across
        // ALL warehouses (billing picks lowest id among active rows),
        // so every warehouse tab edits the same rate.
        const companyWide = feeType.value === 'insurance';
        const existingFee = existingFees
          .filter(fee => (companyWide || fee.warehouse_id === warehouseId) && fee.fee_type === feeType.value)
          .sort((a, b) => {
            // Active rows first: billing only reads active rows, so an
            // inactive canonical row must not shadow the active rate the
            // customer is actually billed.
            const aActive = a.is_active === false ? 1 : 0;
            const bActive = b.is_active === false ? 1 : 0;
            if (aActive !== bActive) return aActive - bActive;
            const aClassed = a.storage_class == null ? 0 : 1;
            const bClassed = b.storage_class == null ? 0 : 1;
            if (aClassed !== bClassed) return aClassed - bClassed;
            return String(a.id) < String(b.id) ? -1 : 1;
          })[0];

        form[warehouseId][feeType.value] = {
          fee_type: feeType.value,
          fee_amount: existingFee?.fee_amount || '',
          billing_unit: existingFee?.billing_unit || feeType.defaultUnit,
          fee_schedule: existingFee?.fee_schedule || feeType.defaultSchedule,
          notes: existingFee?.notes || '',
          id: existingFee?.id || null
        };
      });
    });

    setAllFeesForm(form);
  };

  // Reinitialize form when warehouse access changes
  useEffect(() => {
    if (destinationWarehouseAccess.length > 0) {
      initializeFeesForm(fees); // fees can be empty array, that's fine
    }
  }, [destinationWarehouseAccess]);
  
  // Initialize form when fees data loads
  useEffect(() => {
    if (fees.length > 0 && destinationWarehouseAccess.length > 0) {
      initializeFeesForm(fees);
    }
  }, [fees]);

  // Initialize form for selected warehouse if not already done
  useEffect(() => {
    if (selectedDestinationWarehouseId && !allFeesForm[selectedDestinationWarehouseId] && destinationWarehouseAccess.length > 0) {
      initializeFeesForm(fees);
    }
  }, [selectedDestinationWarehouseId, allFeesForm, destinationWarehouseAccess, fees]);

  // Save all fees for selected warehouse
  const handleSaveAllFees = async () => {
    if (!selectedDestinationWarehouseId) {
      showError('Please select a warehouse first');
      return;
    }

    try {
      const warehouseFees = allFeesForm[selectedDestinationWarehouseId];
      if (!warehouseFees) return;

      const rowLabel = (feeData) => {
        if (feeData.fee_type === 'storage_fee') {
          const slot = feeData.storage_class_slot || feeData.storage_class;
          const scMeta = STORAGE_CLASSES.find((sc) => sc.value === slot);
          return `${scMeta?.label || slot} storage`;
        }
        return feeTypes.find((f) => f.value === feeData.fee_type)?.label || feeData.fee_type;
      };

      // Phase 1: validate every dirty row BEFORE anything is submitted.
      // Fetches fire the moment they are created, so a validation
      // failure discovered mid-loop would leave a partial save behind.
      // Only rows the operator actually CHANGED this session are
      // considered -- submitting every populated row bulk-reactivated
      // unrelated disabled fees, since edited rows save is_active: true.
      const validationErrors = [];
      const rowsToSave = [];
      const present = (value) => value != null && value !== '';

      for (const [compositeKey, feeData] of Object.entries(warehouseFees)) {
        if (!feeData.dirty) continue;

        const hasAmountEntered = present(feeData.fee_amount);
        const amount = parseFloat(feeData.fee_amount);

        // The number inputs carry min="0" but Save All is not a form
        // submission, so nothing enforces it -- a negative or garbled
        // value must error, never silently save as $0.
        if (hasAmountEntered && (!Number.isFinite(amount) || amount < 0)) {
          validationErrors.push(`${rowLabel(feeData)}: rate must be $0 or more`);
          continue;
        }

        // Mirror the backend's up-to-two-decimals rule so a value like
        // 8.501 fails HERE instead of 400ing mid-save after sibling
        // rows have already persisted.
        if (hasAmountEntered && !/^(\d+)?(\.\d{1,2})?$/.test(String(feeData.fee_amount).trim())) {
          validationErrors.push(`${rowLabel(feeData)}: rate can have at most two decimal places`);
          continue;
        }

        // New rows with no amount entered have nothing to create, but an
        // EXPLICIT $0 does create the row -- "available but not charged",
        // the same contract as the default-fee seed (and the future
        // RF4 distinction between a missing rate and a free service).
        // Existing rows save any edit; a cleared amount saves as $0
        // (disabled), never as the stale previous rate.
        if (!feeData.id && !hasAmountEntered) continue;

        // A discount only applies when BOTH fields are set, the rate is
        // positive, and the threshold is a whole number of at least 1
        // (the backend requires isInt min 1) -- billing treats half
        // pairs and $0 rates as unconfigured, so anything else would
        // report success for a discount that silently never applies.
        // Number inputs do not enforce their min/step outside a form
        // submission, so 0, -1, and 1.5 must be caught here rather than
        // 400ing mid-save after sibling rows already persisted.
        if (feeData.fee_type === 'storage_fee') {
          const positiveWholeNumber = (value) =>
            /^\d+$/.test(String(value)) && parseInt(value, 10) >= 1;
          // The rate columns are DECIMAL(10,2): a sub-cent value like
          // 0.001 would round to 0.00 on write and billing would treat
          // the discount as unconfigured, so require currency precision
          // and a $0.01 floor (same rule as the backend validator).
          const currencyRate = (value) =>
            /^(\d+)?(\.\d{1,2})?$/.test(String(value).trim()) && parseFloat(value) >= 0.01;
          const completePair = (rate, threshold) => {
            if (!present(rate) && !present(threshold)) return true;
            return currencyRate(rate) && positiveWholeNumber(threshold);
          };
          if (!completePair(feeData.volume_discount_rate, feeData.volume_discount_threshold)) {
            validationErrors.push(`${rowLabel(feeData)}: volume discount needs a rate of at least $0.01 (max two decimals) and a whole-number pallet threshold of at least 1 (clear both to remove it)`);
            continue;
          }
          if (!completePair(feeData.longterm_discount_rate, feeData.longterm_discount_months)) {
            validationErrors.push(`${rowLabel(feeData)}: long-term discount needs a rate of at least $0.01 (max two decimals) and a whole-number month threshold of at least 1 (clear both to remove it)`);
            continue;
          }
        }

        rowsToSave.push({
          compositeKey,
          feeData,
          sendAmount: hasAmountEntered ? String(feeData.fee_amount) : '0'
        });
      }

      if (validationErrors.length > 0) {
        showError(validationErrors.join('. '));
        return;
      }

      if (rowsToSave.length === 0) {
        showSuccess('No fee changes to save');
        return;
      }

      // Phase 2: submit the validated rows. The composite key for
      // storage rows is `storage_fee|<slot>`; the feeData carries the
      // real fee_type and storage_class fields the backend expects.
      const promises = rowsToSave.map(({ compositeKey, feeData, sendAmount }) => {
        const url = feeData.id
          ? `/api/customer-fees/${customerId}/fees/${feeData.id}`
          : `/api/customer-fees/${customerId}/fees`;

        const method = feeData.id ? 'PUT' : 'POST';

        // RF12: active-catalog fee types have exactly ONE supported
        // (billing_unit, fee_schedule) pair; the backend rejects
        // anything else, and off-spec values hydrated from legacy rows
        // must not fail the save. Legacy/hidden types pass through.
        const catalogType = feeData.fee_type === 'storage_fee'
          ? (storageFeeMeta && { defaultUnit: storageFeeMeta.defaultBillingUnit, defaultSchedule: storageFeeMeta.defaultFeeSchedule })
          : feeTypes.find((f) => f.value === feeData.fee_type);
        const body = {
          warehouse_id: selectedDestinationWarehouseId,
          fee_type: feeData.fee_type || compositeKey,
          fee_amount: sendAmount,
          billing_unit: catalogType ? catalogType.defaultUnit : feeData.billing_unit,
          fee_schedule: catalogType ? catalogType.defaultSchedule : feeData.fee_schedule,
          // min_charge removed from the supported fee model (RF5) --
          // billing no longer applies it and the form no longer edits it.
          notes: feeData.notes || null,
          // Saving through this form means the row is in effect (an
          // explicit $0 is "in effect but not charged"). Without this,
          // editing an inactive row reported success while billing kept
          // ignoring it (is_active was never sent, so it stayed inactive).
          is_active: true
        };

        if (feeData.fee_type === 'storage_fee') {
          // Existing rows send their stored class verbatim; a
          // null-class row OMITS the field so the backend's collision
          // backoff (not the explicit class-change 409) governs any
          // repair against an inactive same-slot sibling. New rows
          // are created at the canonical slot class.
          if (!feeData.id) {
            body.storage_class = feeData.storage_class_slot || 'standard';
          } else if (feeData.storage_class != null) {
            body.storage_class = feeData.storage_class;
          }
          body.volume_discount_rate = feeData.volume_discount_rate || null;
          body.volume_discount_threshold = feeData.volume_discount_threshold || null;
          body.longterm_discount_rate = feeData.longterm_discount_rate || null;
          body.longterm_discount_months = feeData.longterm_discount_months || null;
        }

        return fetch(url, {
          method,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(body)
        }).then(res => res.json());
      });

      const results = await Promise.all(promises);
      const failed = results.filter(result => !result.success);

      if (failed.length === 0) {
        showSuccess('All fees saved successfully');
        fetchData(); // Refresh data
      } else {
        // Surface the backend's guidance instead of only a count --
        // 409s carry an `error` message, express-validator 400s carry
        // an `errors` array.
        const detail = failed
          .map(result => result.error || result.errors?.[0]?.msg)
          .find(Boolean);
        showError(detail
          ? `Failed to save ${failed.length} fee(s): ${detail}`
          : `Failed to save ${failed.length} fee(s)`);
      }
    } catch (error) {
      showError('Failed to save fees');
    }
  };

  // Update fee form data. Marks the row dirty so Save All only submits
  // rows the operator touched -- untouched rows must never be re-saved
  // (or reactivated) as a side effect of editing a different fee.
  const updateFeeForm = (feeType, field, value) => {
    if (!selectedDestinationWarehouseId) return;

    setAllFeesForm(prev => ({
      ...prev,
      [selectedDestinationWarehouseId]: {
        ...prev[selectedDestinationWarehouseId],
        [feeType]: {
          ...prev[selectedDestinationWarehouseId]?.[feeType],
          [field]: value,
          dirty: true
        }
      }
    }));
  };

  const handleDestinationAccessSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`/api/customer-fees/${customerId}/destination-warehouse-access`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(destinationAccessForm)
      });

      const data = await response.json();
      
      if (data.success) {
        showSuccess('Destination warehouse access granted successfully');
        setShowDestinationAccessForm(false);
        setDestinationAccessForm({ warehouse_id: '', notes: '' });
        fetchData();
      } else {
        console.error('API returned error:', data.error);
        showError(data.error || 'Failed to grant destination warehouse access');
      }
    } catch (error) {
      console.error('Full error details:', error);
      showError('Failed to grant destination warehouse access');
    }
  };

  const toggleDestinationWarehouseAccess = async (accessId, currentStatus) => {
    try {
      const response = await fetch(`/api/customer-fees/${customerId}/destination-warehouse-access/${accessId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ is_active: !currentStatus })
      });

      const data = await response.json();
      
      if (data.success) {
        showSuccess(
          `Destination warehouse access ${!currentStatus ? 'enabled' : 'disabled'}`
        );
        fetchData();
      } else {
        showError('Failed to update destination warehouse access');
      }
    } catch (error) {
      showError('Failed to update destination warehouse access');
    }
  };

  // Get available warehouses for access (excluding those already granted access)
  const availableDestinationWarehouses = destinationWarehouses.filter(
    warehouse => !destinationWarehouseAccess.some(access => access.warehouse_id === warehouse.id)
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Destination Warehouse Access Section */}
      {!showFeesOnly && (
        <div className="bg-white shadow-md rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <FiPackage className="mr-2" />
              Destination Warehouse Access
            </h3>
            <PermissionButton
              permissions="edit_customers"
              onClick={() => setShowDestinationAccessForm(true)}
              disabled={availableDestinationWarehouses.length === 0}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              <FiPlus className="mr-1" />
              Grant Access
            </PermissionButton>
          </div>

          {destinationWarehouseAccess.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No destination warehouse access configured</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {destinationWarehouseAccess.map((access) => (
                <div key={access.id} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <div>
                        <h4 className="font-medium text-gray-900">{access.Warehouse.name}</h4>
                        <p className="text-sm text-gray-600">Code: {access.Warehouse.code}</p>
                        {access.Warehouse.address_line1 && (
                          <p className="text-sm text-gray-500">
                            {access.Warehouse.address_line1}
                            {access.Warehouse.city && `, ${access.Warehouse.city}`}
                            {access.Warehouse.state_province && `, ${access.Warehouse.state_province}`}
                          </p>
                        )}
                        {access.notes && (
                          <p className="text-sm text-gray-500 mt-1">{access.notes}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end">
                      <span className={`text-xs font-medium mb-2 ${access.is_active ? 'text-green-600' : 'text-gray-500'}`}>
                        {access.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <button
                        onClick={() => toggleDestinationWarehouseAccess(access.id, access.is_active)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                          access.is_active ? 'bg-blue-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out ${
                            access.is_active ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Fees Section */}
      {!showWarehouseAccessOnly && (
        <div className="bg-white shadow-md rounded-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-medium text-gray-900">
              Customer Fees
            </h3>
          </div>

          {activeDestinationWarehouseIds.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              Please grant destination warehouse access first before configuring fees
            </p>
          ) : (
            <div className="space-y-6">
              {/* Warehouse Selection */}
              <div className="flex items-center space-x-4">
                <label className="block text-sm font-medium text-gray-700 min-w-max">
                  Select Destination Warehouse:
                </label>
                <select
                  value={selectedDestinationWarehouseId}
                  onChange={(e) => setSelectedDestinationWarehouseId(e.target.value)}
                  className="flex-1 max-w-md border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Choose a warehouse...</option>
                  {destinationWarehouses
                    .filter(warehouse => activeDestinationWarehouseIds.includes(warehouse.id))
                    .map(warehouse => (
                      <option key={warehouse.id} value={warehouse.id}>
                        {warehouse.name} ({warehouse.code})
                      </option>
                    ))}
                </select>
              </div>

              {/* Fee List Table */}
              {selectedDestinationWarehouseId && allFeesForm[selectedDestinationWarehouseId] && (
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="text-md font-medium text-gray-900">
                      {destinationWarehouses.find(w => w.id === selectedDestinationWarehouseId)?.name} - Destination Warehouse Fee Schedule
                    </h4>
                    {canMutateBilling && (
                      <button
                        onClick={handleSaveAllFees}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                      >
                        Save All Fees
                      </button>
                    )}
                  </div>

                  {/* Dedicated Storage Rates section. Storage fees
                      need a storage_class and support volume /
                      long-term discount columns, so they get a
                      custom table above the generic fee grid. One
                      row per storage class (standard, oversize,
                      plus any additional classes with existing DB
                      rows). */}
                  {storageFeeMeta && (
                    <div className="mb-6">
                      <h5 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2 px-1">Storage</h5>
                      <p className="mb-2 px-1 text-xs text-gray-500">
                        Per-pallet monthly storage rates. Add one row per storage class. Volume discount applies automatically once the pallet count threshold is met.
                      </p>
                      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Storage Class</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Rate / Pallet / Month</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Volume Discount</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Long-Term Discount</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {Object.entries(allFeesForm[selectedDestinationWarehouseId] || {})
                              .filter(([key, data]) => data.fee_type === 'storage_fee')
                              .sort(([, a], [, b]) => {
                                // Stable order: standard first, then oversize, then everything else.
                                // Sorted by the normalized slot so a legacy-classed winner
                                // (e.g. rackable) still sits in its standard position.
                                const order = { standard: 0, oversize: 1, floor_only: 2, hazmat: 3 };
                                const aSlot = a.storage_class_slot || a.storage_class;
                                const bSlot = b.storage_class_slot || b.storage_class;
                                return (order[aSlot] ?? 99) - (order[bSlot] ?? 99);
                              })
                              .map(([key, feeData]) => {
                                const slotClass = feeData.storage_class_slot || feeData.storage_class;
                                const scMeta = STORAGE_CLASSES.find((sc) => sc.value === slotClass);
                                return (
                                  <tr key={key}>
                                    <td className="px-4 py-2.5">
                                      <div className="text-sm font-medium text-gray-900">{scMeta?.label || slotClass}</div>
                                    </td>
                                    <td className="px-4 py-2.5 text-right">
                                      <div className="flex items-center justify-end">
                                        <span className="text-sm text-gray-400 mr-1">$</span>
                                        <input
                                          type="number"
                                          step="0.01"
                                          min="0"
                                          value={feeData.fee_amount || ''}
                                          onChange={(e) => updateFeeForm(key, 'fee_amount', e.target.value)}
                                          className="w-24 border border-gray-200 rounded px-2 py-1 text-sm text-right focus:ring-blue-500 focus:border-blue-500"
                                          placeholder="0.00"
                                        />
                                      </div>
                                    </td>
                                    <td className="px-4 py-2.5">
                                      {/* The backend treats volume_discount_rate as the REPLACEMENT
                                          dollar rate per pallet once the threshold is met (e.g. $18
                                          instead of $25 at 20+ pallets), matching the default fee
                                          seed. The previous % symbol misrepresented it: entering 10
                                          would have billed $10/pallet, not 10% off. */}
                                      <div className="flex items-center gap-2 text-xs" title="Discounted rate per pallet once the customer stores at least this many pallets">
                                        <span className="text-gray-400">$</span>
                                        <input
                                          type="number"
                                          step="0.01"
                                          min="0.01"
                                          value={feeData.volume_discount_rate || ''}
                                          onChange={(e) => updateFeeForm(key, 'volume_discount_rate', e.target.value)}
                                          className="w-16 border border-gray-200 rounded px-2 py-1 text-xs text-right"
                                          placeholder="rate"
                                        />
                                        <span className="text-gray-500">@</span>
                                        <input
                                          type="number"
                                          step="1"
                                          min="1"
                                          value={feeData.volume_discount_threshold || ''}
                                          onChange={(e) => updateFeeForm(key, 'volume_discount_threshold', e.target.value)}
                                          className="w-14 border border-gray-200 rounded px-2 py-1 text-xs text-right"
                                          placeholder="qty"
                                        />
                                        <span className="text-gray-500">pallets+</span>
                                      </div>
                                    </td>
                                    <td className="px-4 py-2.5">
                                      {/* Same semantics as the volume discount: the value is the
                                          REPLACEMENT dollar rate per pallet once a unit has been
                                          stored at least this many whole calendar months
                                          (billingService.getLongTermRate). These fields were
                                          hydrated and submitted but had no inputs since the fee
                                          UI overhaul -- long-term discounts were unconfigurable. */}
                                      <div className="flex items-center gap-2 text-xs" title="Discounted rate per pallet once a unit has been stored for at least this many whole calendar months">
                                        <span className="text-gray-400">$</span>
                                        <input
                                          type="number"
                                          step="0.01"
                                          min="0.01"
                                          value={feeData.longterm_discount_rate || ''}
                                          onChange={(e) => updateFeeForm(key, 'longterm_discount_rate', e.target.value)}
                                          className="w-16 border border-gray-200 rounded px-2 py-1 text-xs text-right"
                                          placeholder="rate"
                                        />
                                        <span className="text-gray-500">after</span>
                                        <input
                                          type="number"
                                          step="1"
                                          min="1"
                                          value={feeData.longterm_discount_months || ''}
                                          onChange={(e) => updateFeeForm(key, 'longterm_discount_months', e.target.value)}
                                          className="w-14 border border-gray-200 rounded px-2 py-1 text-xs text-right"
                                          placeholder="mo"
                                        />
                                        <span className="text-gray-500">months+</span>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Serial capture, MAC capture, and relabel are
                      now rolled into Handling — Inbound (no separate
                      fees). They remain available as checkboxes on
                      the ASN form so warehouse staff still act on
                      them; this is just a visual note. */}
                  <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
                    <h5 className="text-sm font-semibold text-blue-900">Inbound Receiving Services</h5>
                    <p className="mt-1 text-xs text-blue-800">
                      Serial number capture, MAC address capture, and relabel work are <strong>included in Handling — Inbound</strong> and are not billed separately. Customers still request them per-ASN via form checkboxes so warehouse staff know what to do; the cost is covered by the per-pallet inbound handling rate.
                    </p>
                  </div>

                  <p className="mb-4 text-xs text-gray-500">
                    These warehouse rates govern storage, fulfillment, and optional inbound receiving services for work routed through this destination warehouse.
                  </p>

                  {/* Group fees by category. Storage is rendered
                      separately above because its rows have extra
                      columns. Under the simplified catalog this
                      loop effectively only renders the Handling
                      group (Handling — Inbound, Handling — Outbound,
                      Additional Labour). */}
                  {['Handling', 'Insurance']
                    .filter(category => feeTypes.some(f => f.category === category))
                    .map(category => {
                    const categoryFees = feeTypes.filter(f => f.category === category);
                    return (
                      <div key={category} className="mb-6">
                        <h5 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2 px-1">{category}</h5>
                        {category === 'Inbound Receiving Options' && (
                          <p className="mb-2 px-1 text-xs text-amber-700">
                            These one-time fees are applied when an ASN or manifest explicitly requests serial capture, MAC capture, or relabel service during receiving.
                          </p>
                        )}
                        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                          <table className="min-w-full divide-y divide-gray-200 table-fixed">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="w-48 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fee</th>
                                <th className="w-24 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                <th className="w-28 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Schedule</th>
                                <th className="w-28 px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                                <th className="w-14 px-2 py-2"></th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {categoryFees.map(feeType => {
                                const feeData = allFeesForm[selectedDestinationWarehouseId][feeType.value] || {};
                                const schedule = feeData.fee_schedule || feeType.defaultSchedule;
                                const amount = parseFloat(feeData.fee_amount) || 0;
                                const isActive = amount > 0;

                                return (
                                  <tr key={feeType.value} className={isActive ? '' : 'text-gray-400'}>
                                    <td className="w-48 px-4 py-2.5">
                                      <div className="text-sm font-medium truncate">{feeType.label}</div>
                                      <div className="text-xs text-gray-400">{(feeData.billing_unit || feeType.defaultUnit).replace(/_/g, ' ')}</div>
                                    </td>
                                    <td className="w-24 px-4 py-2.5">
                                      <span className={`text-xs px-1.5 py-0.5 rounded ${schedule === 'one_time' ? 'bg-gray-100 text-gray-600' : 'bg-blue-50 text-blue-600'}`}>
                                        {schedule === 'one_time' ? 'One-time' : 'Recurring'}
                                      </span>
                                    </td>
                                    <td className="w-28 px-4 py-2.5">
                                      {/* RF12: each active fee type has exactly one supported
                                          schedule -- billing implements nothing else, so the
                                          old 6-option dropdown promised behavior that never
                                          happened. */}
                                      <span className="text-sm text-gray-600">
                                        {feeSchedules.find(s => s.value === feeType.defaultSchedule)?.label || feeType.defaultSchedule}
                                      </span>
                                    </td>
                                    <td className="w-28 px-4 py-2.5 text-right">
                                      <div className="flex items-center justify-end">
                                        <span className="text-sm text-gray-400 mr-1">$</span>
                                        <input
                                          type="number"
                                          step="0.01"
                                          min="0"
                                          value={feeData.fee_amount || ''}
                                          onChange={(e) => updateFeeForm(feeType.value, 'fee_amount', e.target.value)}
                                          className="w-20 border border-gray-200 rounded px-2 py-1 text-sm text-right focus:ring-blue-500 focus:border-blue-500"
                                          placeholder="0.00"
                                        />
                                      </div>
                                    </td>
                                    <td className="w-14 px-2 py-2.5">
                                      <div className="flex items-center justify-end space-x-1">
                                        {feeData.notes ? (
                                          <span className="relative group">
                                            <FiInfo className="h-4 w-4 text-blue-400 cursor-help" />
                                            <span className="absolute bottom-full right-0 mb-1 px-2 py-1 text-xs text-white bg-gray-800 rounded max-w-xs whitespace-normal opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                                              {feeData.notes}
                                            </span>
                                          </span>
                                        ) : (
                                          <span className="w-4" />
                                        )}
                                        <button
                                          onClick={() => {
                                            const note = prompt('Fee notes:', feeData.notes || '');
                                            if (note !== null) updateFeeForm(feeType.value, 'notes', note);
                                          }}
                                          className="text-gray-400 hover:text-gray-600"
                                          aria-label={`Edit notes for ${feeType.label}`}
                                        >
                                          <FiEdit2 className="h-3.5 w-3.5" />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Access Form Modal */}
      {showDestinationAccessForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Grant Destination Warehouse Access</h3>
              <p className="text-sm text-gray-500 mb-4">Choose which internal destination warehouses this customer can route work into.</p>
              <form onSubmit={handleDestinationAccessSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Destination Warehouse</label>
                  <select
                    value={destinationAccessForm.warehouse_id}
                    onChange={(e) => setDestinationAccessForm(prev => ({ ...prev, warehouse_id: e.target.value }))}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Warehouse</option>
                    {availableDestinationWarehouses.map(warehouse => (
                      <option key={warehouse.id} value={warehouse.id}>
                        {warehouse.name} ({warehouse.code})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Notes</label>
                  <textarea
                    value={destinationAccessForm.notes}
                    onChange={(e) => setDestinationAccessForm(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Optional notes about this access grant..."
                  />
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowDestinationAccessForm(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Grant Access
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerFeesManager; 
